/**
 * @file ElementTypeFields.tsx
 * @description «Цвет и шрифт» элемента по частям (phase19.16, партия SB120,
 * этап 3а «элементы»; site-builder.md SB120, SB8, SB10; проект UI
 * uiux-sb120-stage3 §2.1–§2.2, §8; данные db-design-sb120-stage3 §2):
 *
 * ```
 * Часть [ Название | Подпись и кнопка ]     ← Segmented 48, только у многочастных
 * Цвет текста · Шрифт · Размер (+ на телефоне) · Разрядка — TypoPartFields
 * ```
 *
 * Части — из реестра shared (`elementFontParts` → `key` ⊆ `TYPO_PART_KEYS`):
 * у одночастных видов переключателя нет. Одно поле на элемент, шапку
 * раздела и надпись на фото: общий контракт `typo` / `onChange(typo)`.
 * Значение хранится каноном shared (`compactTypo`): «↺» снимает ключ, пустая
 * часть и пустой `typo` не пишутся.
 *
 * Состояния: оформление сайта ещё не пришло — «Загружаем цвета и шрифты
 * сайта…» (группа не исчезает); список своих шрифтов не загрузился — полоса
 * с «Повторить» внутри `FontField`; свой шрифт удалён — ✕ у группы и
 * причина в карточке шрифта. Переход к замечанию (`revealPath`, в том числе
 * 422 `element_font_not_found`) под `<путь>.typo.<часть>` выбирает эту часть.
 */

import type { FontPart, TypoValue } from "@chaiberi/shared";
import { useEffect, useState } from "react";

import { Label } from "../../../ui/label";
import { Skeleton } from "../../../ui/skeleton";
import { Segmented } from "../../../site-view/Segmented";
import { useShopFonts } from "../../../site-view/shop-fonts-context";
import type { NumberPreset } from "../../../site-fields/NumberPresets";
import { useSectionForm } from "../section-form-context";
import { TypoPartFields } from "./TypoPartFields";
import { patchTypoPart } from "../../../../lib/site-builder/element-typo";
import { elementWarningsUnder, familiesOnPage } from "../../../../lib/site-builder/element-warnings";
import type { TypoBase } from "../../../../lib/site-builder/typo-base";

export const TYPO_LOADING_TEXT = "Загружаем цвета и шрифты сайта…";

export interface ElementTypeFieldsProps {
  /** Части с ключом `typo` (порядок — как в реестре). */
  parts: readonly FontPart[];
  typo: TypoValue | undefined;
  onChange: (typo: TypoValue | undefined) => void;
  /** Путь владельца `typo` в payload: элемент, `header`, надпись на фото. */
  path: string;
  /** Размер «как у роли» части. */
  baseOf: (key: string) => TypoBase;
  /** Ступени размера части (заголовки — [H3][H2][H1]). */
  presetsOf?: (key: string) => readonly NumberPreset[] | undefined;
  /** Фон под текстом: `#hex`, `"photo"` или неизвестен. */
  elementBg?: string;
  colorAutoLabel?: string;
  /** Цвет «Авто», если особый (кнопка: по заливке / как ссылка). */
  colorAuto?: string;
  colorHint?: string | null;
}

export function ElementTypeFields(props: ElementTypeFieldsProps) {
  const { parts, typo, onChange, path } = props;
  const { theme, elementWarnings, revealPath } = useSectionForm();
  const { registry } = useShopFonts();
  const keyed = parts.filter((p): p is FontPart & { key: string } => p.key !== undefined);
  const [picked, setPicked] = useState<string | null>(null);
  const current = keyed.find((p) => p.key === picked) ?? keyed[0];
  // Переход из списка замечаний к полю части — показать эту часть.
  const prefix = `${path}.typo.`;
  const revealKey = revealPath?.startsWith(prefix) ? revealPath.slice(prefix.length).split(".")[0] : undefined;
  useEffect(() => {
    if (revealKey) setPicked(revealKey);
  }, [revealKey]);

  if (!current) return null;
  if (!theme) {
    return (
      <div className="flex flex-col gap-2" role="status">
        <p className="type-caption text-on-surface-variant">{TYPO_LOADING_TEXT}</p>
        <Skeleton className="h-12 rounded-lg" />
        <Skeleton className="h-12 rounded-lg" />
      </div>
    );
  }
  const partPath = `${path}.typo.${current.key}`;

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {keyed.length > 1 ? (
        <div className="flex min-w-0 flex-col gap-1">
          <Label>Часть</Label>
          <Segmented aria-label="Часть элемента" options={keyed.map((p) => ({ value: p.key, label: p.part || p.key }))} value={current.key} onChange={setPicked} />
        </div>
      ) : null}
      <TypoPartFields
        key={current.key}
        part={current}
        value={typo?.[current.key]}
        onPatch={(patch) => onChange(patchTypoPart(typo, current.key, patch))}
        path={partPath}
        base={props.baseOf(current.key)}
        theme={theme}
        registry={registry}
        elementBg={props.elementBg}
        colorAutoLabel={props.colorAutoLabel}
        colorAuto={props.colorAuto}
        colorHint={props.colorHint}
        sizePresets={props.presetsOf?.(current.key)}
        warnings={elementWarningsUnder(elementWarnings, partPath)}
        familiesOnPage={familiesOnPage(elementWarnings)}
      />
    </div>
  );
}
